/**
 * Service to monitor and handle network connection status
 * Provides methods to check connection status and register callbacks.
 *
 * Offline detection is intentionally driven by failed API calls
 * (see RequestService) instead of the browser `online`/`offline` events,
 * because `navigator.onLine` and those events are unreliable and produce
 * false positives that trigger the blocking offline modal.
 */
class ConnectionService {
  constructor() {
    // Start optimistic and let RequestService drive all transitions. Seeding
    // from `navigator.onLine` would contradict the API-driven design above
    // and would throw in non-browser environments (e.g. unit tests) where
    // `navigator` is undefined.
    this.isOnline = true;
    this.connectionListeners = [];
  }

  /**
   * Report that an API call failed because of a network error.
   * Notifies listeners only on the transition from online to offline.
   */
  reportConnectionFailure() {
    if (this.isOnline) {
      this.isOnline = false;
      this.notifyListeners();
    }
  }

  /**
   * Report that an API call succeeded, confirming connectivity.
   * Notifies listeners only on the transition from offline to online.
   */
  reportConnectionRestored() {
    if (!this.isOnline) {
      this.isOnline = true;
      this.notifyListeners();
    }
  }

  /**
   * Register a callback function to be called when connection status changes
   * @param {Function} callback - Function to call when connection status changes
   */
  registerConnectionListener(callback) {
    if (typeof callback === 'function' && !this.connectionListeners.includes(callback)) {
      this.connectionListeners.push(callback);
    }
  }

  /**
   * Remove a previously registered callback
   * @param {Function} callback - Function to remove from listeners
   */
  unregisterConnectionListener(callback) {
    const index = this.connectionListeners.indexOf(callback);
    if (index !== -1) {
      this.connectionListeners.splice(index, 1);
    }
  }

  /**
   * Notify all registered listeners about connection status change
   */
  notifyListeners() {
    this.connectionListeners.forEach(callback => {
      callback(this.isOnline);
    });
  }

  /**
   * Get current connection status
   * @returns {boolean} - True if online, false if offline
   */
  getConnectionStatus() {
    return this.isOnline;
  }

  /**
   * Probe connectivity by requesting a lightweight, always-available resource.
   * This is a pure probe: it never mutates `isOnline` nor notifies listeners,
   * so the API-driven offline state remains authoritative and cannot be
   * desynchronized by a transient probe failure.
   * @returns {Promise<boolean>} - Promise resolving to true if reachable.
   */
  async checkActiveConnection() {
    // No way to probe connectivity outside a browser (e.g. unit tests running
    // without jsdom); assume reachable instead of throwing.
    if (typeof window === 'undefined' || typeof fetch === 'undefined') {
      return true;
    }

    try {
      // Use our own favicon to check connectivity
      // This is a lightweight resource that should always be available
      const baseUrl = window.location.origin;
      const faviconUrl = `${baseUrl}/favicon.ico`;

      // Make a HEAD request to our favicon
      await fetch(faviconUrl, {
        method: 'HEAD', // Only request headers, not content
        mode: 'no-cors',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      return true;
    } catch (error) {
      return false;
    }
  }
}

// Create singleton instance
const connectionService = new ConnectionService();

export default connectionService;
